// BUILD-046 task 8.20 (supporting model): the two LABELLED counts inside the HQ views (design "Frontend Design" slice scope: "the Tasks bell appears
// in S1 only as labelled open and shared-queue counts inside HQ views"; Req 12.3, 12.4, 12.18, 12.19, 2.12).
//
//   Open tasks   : the Caller's open total (`openTaskCount` of `GET /work/bell`)
//   Shared queue : the unclaimed Shared_Tasks the Caller can take (`sharedQueueCount`)
// They are two numbers with two labels and are NEVER summed: this module has no total, and `describeCounts` exposes none. A count that cannot be
// read is "Unavailable" (with a Retry), never 0 (listStateModel.countReducer). Both counts come from one request, so a failure makes both
// "Unavailable" together; a number is never kept from an earlier answer. Oversight adds nothing to either: they are the server's counts for the
// Caller, and an overseer's view of other people's tasks is not part of them (Req 7.7, 14.19).
//
// They refresh when the Caller changes (cleared at once, then loaded for the new Caller; an answer for the previous Caller is dropped) and on
// `shared-tasks:changed` (a Done, a claim, an assignment, an action: the host calls `load`).
//
// Framework-free so it can be tested without a DOM; useWorkCounts.ts wraps it for React. Erasable TypeScript; runtime imports: the list-state model.

import { countReducer, describeCount, initialCountState, isStaleOutcome, toListError } from './listStateModel';
import type { CountState, CountView } from './listStateModel';

export interface CountsState {
  open: CountState;
  shared: CountState;
}

export function initialCounts(): CountsState {
  return { open: initialCountState(), shared: initialCountState() };
}

export interface CountsView {
  open: { label: 'Open tasks'; count: CountView };
  shared: { label: 'Shared queue'; count: CountView };
  /** True when either number could not be read: the Retry control is offered. */
  canRetry: boolean;
}

/** What the strip shows. There is deliberately no `total` here. */
export function describeCounts(state: CountsState): CountsView {
  const open = describeCount(state.open);
  const shared = describeCount(state.shared);
  return {
    open: { label: 'Open tasks', count: open },
    shared: { label: 'Shared queue', count: shared },
    canRetry: open.kind === 'unavailable' || shared.kind === 'unavailable',
  };
}

export interface CountsControllerDeps {
  getEpoch: () => number;
  /** `workApi.getBell`. */
  getBell: () => Promise<{ openTaskCount: number; sharedQueueCount: number }>;
}

export interface CountsController {
  getState: () => CountsState;
  subscribe: (listener: () => void) => () => void;
  /** Requests both counts (mount, Retry, `shared-tasks:changed`). */
  load: () => Promise<void>;
  /** A Caller change: both counts are cleared at once and requested for the new Caller. */
  callerChanged: () => Promise<void>;
}

export function createCountsController(deps: CountsControllerDeps): CountsController {
  let state = initialCounts();
  const listeners = new Set<() => void>();
  const publish = (next: CountsState): void => {
    if (next === state) return;
    state = next;
    for (const listener of [...listeners]) listener();
  };

  async function load(): Promise<void> {
    const epoch = deps.getEpoch();
    const seq = state.open.epoch === epoch ? state.open.seq + 1 : 1;
    publish({ open: countReducer(state.open, { type: 'request', epoch, seq }), shared: countReducer(state.shared, { type: 'request', epoch, seq }) });
    let bell: { openTaskCount: number; sharedQueueCount: number };
    try {
      bell = await deps.getBell();
    } catch (thrown) {
      // Dropped because the Caller changed: neither a count nor a failure. The Caller change has already requested the counts again.
      if (isStaleOutcome(thrown)) return;
      const error = toListError(thrown);
      publish({ open: countReducer(state.open, { type: 'failure', epoch, seq, error }), shared: countReducer(state.shared, { type: 'failure', epoch, seq, error }) });
      return;
    }
    const open = bell === null || bell === undefined ? NaN : bell.openTaskCount;
    const shared = bell === null || bell === undefined ? NaN : bell.sharedQueueCount;
    // A value that is not a count is a failure for THAT number (countReducer), never a 0.
    publish({
      open: countReducer(state.open, { type: 'success', epoch, seq, value: open }),
      shared: countReducer(state.shared, { type: 'success', epoch, seq, value: shared }),
    });
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    load,
    callerChanged: load,
  };
}
